import {
  Controller,
  useController,
  useFormContext,
  useFormState,
} from "react-hook-form";
import { Fragment, FunctionComponent, useState } from "react";
import { getError } from "../utils";
import {
  OptionInterface,
  StyledArrow,
  StyledDropdown,
  StyledInner,
  StyledLock,
  StyledText,
  StyledWrapper,
} from "./dropdown.styles";
import Warning from "../warning";
import Label from "../label";
import Field from "../field";
import { faCaretDown, faLock } from "components/icon-library";

interface DropdownProps {
  name: string;
  label: string;
  disabled?: boolean;
  required?: boolean;
  options: OptionInterface[];
}

const Dropdown: FunctionComponent<DropdownProps> = ({
  name,
  label,
  disabled = false,
  required = false,
  options,
}) => {
  // other states
  const [hover, toggleHover] = useState(false);
  const [focus, toggleFocus] = useState(false);
  const { control, setValue, trigger } = useFormContext();
  const { field } = useController({ name, control });
  const { errors } = useFormState();

  const onChange = (e: any) => {
    // don't change, if disabled
    if (disabled) {
      return;
    }

    // set the value to state (not sure if needed?)
    setValue(name, e.target.value);

    // delegate
    field.onChange(e);

    // trigger validation
    trigger(name);
  };

  const error = getError(errors, name);

  return (
    <Fragment>
      <Controller
        key={name}
        name={name}
        control={control}
        render={({ field: { ref, ...fieldProps } }) => {
          const selectedOption = options.find(
            (o) => o.value === fieldProps.value,
          );
          const selectedText = selectedOption ? selectedOption.text : "";

          const isEmpty = selectedText === "";
          return (
            <Field>
              <Label name={name} required={required} disabled={disabled}>
                {label}
              </Label>
              <StyledWrapper>
                {disabled === true && <StyledLock icon={faLock} />}
                {disabled === false && <StyledArrow icon={faCaretDown} />}
                <StyledText
                  $empty={isEmpty}
                  $hover={hover}
                  $focus={focus}
                  $disabled={disabled}
                >
                  <StyledInner>
                    {isEmpty && "Valitse..."}
                    {(!isEmpty && selectedText) || <Fragment>&nbsp;</Fragment>}
                  </StyledInner>
                </StyledText>
                <StyledDropdown
                  key={name}
                  id={`id_${name}`}
                  ref={ref}
                  disabled={disabled}
                  value={fieldProps.value}
                  onChange={onChange}
                  name={fieldProps.name}
                  // some aria flags
                  aria-required={required}
                  aria-label={label}
                  aria-invalid={error !== undefined}
                  onMouseEnter={() => toggleHover(true)}
                  onMouseLeave={() => toggleHover(false)}
                  onFocus={() => toggleFocus(true)}
                  onBlur={() => toggleFocus(false)}
                >
                  {/* in case of empty value (initial case) add an empty option */}
                  {field.value === "" && (
                    <option value="" disabled>
                      Valitse...
                    </option>
                  )}

                  {/* add the actual options */}
                  {options.map((option) => {
                    return (
                      <option
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled || false}
                      >
                        {option.text}
                      </option>
                    );
                  })}
                </StyledDropdown>
              </StyledWrapper>
              <Warning name={name} />
            </Field>
          );
        }}
      />
    </Fragment>
  );
};

export default Dropdown;
